'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { useDayPicker, useNavigation } from 'react-day-picker';

import { buttonVariants } from '@ui/components/ui/button';
import { cn } from '@ui/lib/utils';

interface CalendarCaptionProps {
  shortenMonthNames?: boolean;
  locale?: string;
}

export function CalendarCaption({ shortenMonthNames = false, locale = 'en' }: CalendarCaptionProps) {
  const { goToMonth, nextMonth, previousMonth, currentMonth } = useNavigation();
  const { fromYear, toYear } = useDayPicker();

  const currentYear = currentMonth.getFullYear();
  const currentMonthIndex = currentMonth.getMonth();

  // Generate localized month names dynamically with first letter capitalized
  const displayMonthNames = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const date = new Date(2000, i, 1);
      const monthName = date.toLocaleDateString(locale, { month: shortenMonthNames ? 'short' : 'long' });
      // Capitalize first letter for display (French months are lowercase by convention)
      return monthName.charAt(0).toUpperCase() + monthName.slice(1);
    });
  }, [locale, shortenMonthNames]);

  const handlePreviousMonth = () => {
    if (!previousMonth) return;
    goToMonth(previousMonth);
  };

  const handleNextMonth = () => {
    if (!nextMonth) return;
    goToMonth(nextMonth);
  };

  const handlePreviousYear = () => {
    const newDate = new Date(currentYear - 1, currentMonthIndex);
    if (fromYear && newDate.getFullYear() < fromYear) return;
    goToMonth(newDate);
  };

  const handleNextYear = () => {
    const newDate = new Date(currentYear + 1, currentMonthIndex);
    if (toYear && newDate.getFullYear() > toYear) return;
    goToMonth(newDate);
  };

  const isPreviousMonthDisabled = !previousMonth;
  const isNextMonthDisabled = !nextMonth;
  const isPreviousYearDisabled = fromYear ? currentYear <= fromYear : false;
  const isNextYearDisabled = toYear ? currentYear >= toYear : false;

  return (
    <div className="flex justify-center items-center gap-4 w-full pt-1">
      {/* Month Navigation */}
      <div className="flex justify-center items-center gap-1">
        <button
          type="button"
          onClick={handlePreviousMonth}
          disabled={isPreviousMonthDisabled}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'size-7 bg-transparent p-0 opacity-50 hover:opacity-100 disabled:opacity-25',
            !isPreviousMonthDisabled && 'cursor-pointer'
          )}
          aria-label="Go to previous month"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="text-sm font-medium min-w-[90px] text-center">{displayMonthNames[currentMonthIndex]}</span>
        <button
          type="button"
          onClick={handleNextMonth}
          disabled={isNextMonthDisabled}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'size-7 bg-transparent p-0 opacity-50 hover:opacity-100 disabled:opacity-25',
            !isNextMonthDisabled && 'cursor-pointer'
          )}
          aria-label="Go to next month"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      {/* Year Navigation */}
      <div className="flex justify-center items-center gap-1">
        <button
          type="button"
          onClick={handlePreviousYear}
          disabled={isPreviousYearDisabled}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'size-7 bg-transparent p-0 opacity-50 hover:opacity-100 disabled:opacity-25',
            !isPreviousYearDisabled && 'cursor-pointer'
          )}
          aria-label="Go to previous year"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="text-sm font-medium min-w-[50px] text-center">{currentYear}</span>
        <button
          type="button"
          onClick={handleNextYear}
          disabled={isNextYearDisabled}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'size-7 bg-transparent p-0 opacity-50 hover:opacity-100 disabled:opacity-25',
            !isNextYearDisabled && 'cursor-pointer'
          )}
          aria-label="Go to next year"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
    </div>
  );
}
